/**
 * TravelTransitBookingLinks — "where do I actually buy this ticket" helper.
 *
 * Renders a small panel inside TravelTransitEditor that suggests public
 * booking sites appropriate to the selected mode. We don't integrate with
 * any ticketing API — the user still books on the site itself — we just
 * pre-fill the search URL using the transit's from / to / date so they
 * can click through and compare. This is the "at least instruct the user
 * on local sites to source tickets" fallback spec'd on the feature ask.
 *
 * Modes covered:
 *   Flight  → Google Flights, Skyscanner, Kayak
 *   Train   → Trainline (global), Rail Europe (EU), Amtrak (US),
 *             plus a country hint for the local rail operator when the
 *             from/to country is recognisable
 *   Bus     → FlixBus + Rome2Rio
 *   Ferry   → FerryHopper + Rome2Rio
 *   Drive / Helicopter / Other → Rome2Rio multi-modal fallback + tips
 */

import { ExternalLink, Info } from 'lucide-react';
import type { TravelItemResponse } from '../api';

interface BookingLink {
  label: string;
  url: string;
  note?: string;
}

/**
 * When the destination text mentions a known country, add a pointer to
 * the country's incumbent rail operator. Rail searches on foreign sites
 * often surface cheaper fares than the aggregators do.
 */
const LOCAL_RAIL_HINTS: Array<{ match: RegExp; label: string; url: string }> = [
  {
    match: /\b(france|paris|lyon|marseille|nice|bordeaux)\b/i,
    label: 'SNCF Connect (France)',
    url: 'https://www.sncf-connect.com/en-en/',
  },
  {
    match: /\b(germany|berlin|munich|frankfurt|hamburg|köln|cologne)\b/i,
    label: 'Deutsche Bahn (Germany)',
    url: 'https://www.bahn.com/en',
  },
  {
    match: /\b(spain|madrid|barcelona|seville|valencia|sevilla)\b/i,
    label: 'Renfe (Spain)',
    url: 'https://www.renfe.com/es/en',
  },
  {
    match: /\b(italy|rome|milan|florence|venice|naples|firenze)\b/i,
    label: 'Trenitalia (Italy)',
    url: 'https://www.trenitalia.com/en.html',
  },
  {
    match: /\b(switzerland|zurich|geneva|bern|basel|lucerne)\b/i,
    label: 'SBB (Switzerland)',
    url: 'https://www.sbb.ch/en',
  },
  {
    match: /\b(uk|united kingdom|england|london|manchester|edinburgh|glasgow|scotland)\b/i,
    label: 'National Rail (UK)',
    url: 'https://www.nationalrail.co.uk/',
  },
  {
    match: /\b(japan|tokyo|kyoto|osaka|hokkaido|nagoya)\b/i,
    label: 'JR (Japan Rail)',
    url: 'https://japanrailpass.net/en/',
  },
  {
    match: /\b(netherlands|amsterdam|rotterdam|utrecht|the hague)\b/i,
    label: 'NS (Netherlands)',
    url: 'https://www.ns.nl/en',
  },
  {
    match: /\b(austria|vienna|salzburg|graz|innsbruck)\b/i,
    label: 'ÖBB (Austria)',
    url: 'https://www.oebb.at/en/',
  },
  {
    match: /\b(belgium|brussels|antwerp|bruges|ghent)\b/i,
    label: 'SNCB (Belgium)',
    url: 'https://www.belgiantrain.be/en',
  },
  {
    match: /\b(sweden|stockholm|gothenburg|malmö)\b/i,
    label: 'SJ (Sweden)',
    url: 'https://www.sj.se/en/home.html',
  },
  {
    match: /\b(india|mumbai|delhi|kolkata|chennai|bangalore|bengaluru)\b/i,
    label: 'IRCTC (India)',
    url: 'https://www.irctc.co.in/',
  },
  {
    match: /\b(china|beijing|shanghai|guangzhou|shenzhen|xi'an|xian)\b/i,
    label: 'China Railway 12306',
    url: 'https://www.12306.cn/en/index.html',
  },
];

/**
 * Extract a YYYY-MM-DD date from a free-text field. Transit departures are
 * stored as free text ("14:30", "2026-05-01 14:30", ISO timestamps), so we
 * accept whatever the user typed and look for a date-shaped substring.
 */
function extractDate(text: string | null | undefined): string | null {
  if (!text) return null;
  const iso = text.match(/(\d{4}-\d{2}-\d{2})/);
  return iso ? iso[1] : null;
}

function buildFlightLinks(from: string, to: string, date: string | null): BookingLink[] {
  const fromEnc = encodeURIComponent(from);
  const toEnc = encodeURIComponent(to);
  const datePart = date ? ` on ${date}` : '';
  const gfQuery = encodeURIComponent(`flights from ${from} to ${to}${datePart}`);
  const links: BookingLink[] = [
    { label: 'Google Flights', url: `https://www.google.com/travel/flights?q=${gfQuery}` },
    {
      label: 'Skyscanner',
      url: `https://www.skyscanner.com/transport/flights-from/${fromEnc}/${toEnc}/`,
    },
    {
      label: 'Kayak',
      url: date
        ? `https://www.kayak.com/flights/${fromEnc}-${toEnc}/${date}`
        : `https://www.kayak.com/flights/${fromEnc}-${toEnc}`,
    },
  ];
  return links;
}

function buildTrainLinks(from: string, to: string, date: string | null): BookingLink[] {
  const fromEnc = encodeURIComponent(from);
  const toEnc = encodeURIComponent(to);
  const dateSuffix = date ? `&outwardDate=${date}` : '';
  const links: BookingLink[] = [
    {
      label: 'Trainline',
      url: `https://www.thetrainline.com/book/results?origin=${fromEnc}&destination=${toEnc}${dateSuffix}`,
      note: 'Best general-purpose for Europe + UK.',
    },
    {
      label: 'Rail Europe',
      url: `https://www.raileurope.com/en/search?origin-station=${fromEnc}&destination-station=${toEnc}${date ? `&date=${date}` : ''}`,
    },
    {
      label: 'Amtrak',
      url: 'https://www.amtrak.com/tickets/departure.html',
      note: 'For US routes — the site does not accept deep-link params, so search manually.',
    },
  ];
  // Inject country-specific hint if we can guess one.
  const hint = LOCAL_RAIL_HINTS.find((h) => h.match.test(`${from} ${to}`));
  if (hint) {
    links.unshift({
      label: hint.label,
      url: hint.url,
      note: 'Local operator — often cheaper than aggregators.',
    });
  }
  return links;
}

function buildBusLinks(from: string, to: string, date: string | null): BookingLink[] {
  const fromEnc = encodeURIComponent(from);
  const toEnc = encodeURIComponent(to);
  return [
    {
      label: 'FlixBus',
      url: `https://www.flixbus.com/search?departureCity=${fromEnc}&arrivalCity=${toEnc}${date ? `&rideDate=${date}` : ''}`,
      note: 'Dominant intercity bus network in Europe + parts of the US.',
    },
    {
      label: 'Rome2Rio',
      url: `https://www.rome2rio.com/s/${fromEnc}/${toEnc}`,
      note: 'Multi-modal — compares buses against trains and flights.',
    },
  ];
}

function buildFerryLinks(from: string, to: string): BookingLink[] {
  const fromEnc = encodeURIComponent(from);
  const toEnc = encodeURIComponent(to);
  return [
    {
      label: 'FerryHopper',
      url: `https://www.ferryhopper.com/en/ferries/${fromEnc}/${toEnc}`,
      note: 'Mediterranean + Greek islands coverage.',
    },
    {
      label: 'Direct Ferries',
      url: `https://www.directferries.com/`,
      note: 'Global aggregator — search manually.',
    },
    {
      label: 'Rome2Rio',
      url: `https://www.rome2rio.com/s/${fromEnc}/${toEnc}`,
    },
  ];
}

function buildRome2RioOnly(from: string, to: string): BookingLink[] {
  const fromEnc = encodeURIComponent(from);
  const toEnc = encodeURIComponent(to);
  return [
    {
      label: 'Rome2Rio',
      url: `https://www.rome2rio.com/s/${fromEnc}/${toEnc}`,
      note: 'Multi-modal routing — a good starting point when the mode is unusual.',
    },
  ];
}

function getLinksForMode(
  mode: string | null,
  from: string,
  to: string,
  date: string | null,
): BookingLink[] {
  switch (mode) {
    case 'Flight':
      return buildFlightLinks(from, to, date);
    case 'Train':
      return buildTrainLinks(from, to, date);
    case 'Bus':
      return buildBusLinks(from, to, date);
    case 'Ferry':
      return buildFerryLinks(from, to);
    case 'Drive':
    case 'Helicopter':
    case 'Other':
      return buildRome2RioOnly(from, to);
    default:
      return [];
  }
}

interface TravelTransitBookingLinksProps {
  item: TravelItemResponse;
}

export function TravelTransitBookingLinks({ item }: TravelTransitBookingLinksProps) {
  const mode = item.transitMode || null;
  const from = item.transitFrom?.trim() || '';
  const to = item.transitTo?.trim() || '';
  const date = extractDate(item.transitDeparture);

  // Only render when we have at least a mode; from/to are nice-to-have but we
  // still show mode-appropriate sites if they're missing so the user can open
  // the site and search manually.
  if (!mode) return null;

  const links = getLinksForMode(mode, from || '—', to || '—', date);
  if (links.length === 0) return null;

  const needsFromTo = !from || !to;

  return (
    <div className="border border-border rounded-md p-3 space-y-2 bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Book this {mode.toLowerCase()}
        </div>
        {date && <span className="text-[10px] text-muted-foreground">Date: {date}</span>}
      </div>

      {needsFromTo && (
        <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="h-3 w-3 mt-0.5 shrink-0" />
          <span>
            Fill in From and To to pre-fill the search — otherwise you will land on the site's
            homepage.
          </span>
        </div>
      )}

      <ul className="space-y-1.5">
        {links.map((link) => (
          <li key={link.url}>
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer noopener"
              className="group flex items-start gap-2 text-sm hover:bg-muted/40 rounded px-2 py-1 -mx-2"
            >
              <ExternalLink className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground group-hover:text-foreground" />
              <div className="flex-1 min-w-0">
                <div className="font-medium group-hover:underline">{link.label}</div>
                {link.note && <div className="text-xs text-muted-foreground">{link.note}</div>}
              </div>
            </a>
          </li>
        ))}
      </ul>

      <div className="text-[10px] text-muted-foreground pt-1 border-t border-border">
        Links open on each provider's site — we don't process bookings.
      </div>
    </div>
  );
}

// Exported for tests / external consumers if we ever need them.
export const __internals = {
  extractDate,
  getLinksForMode,
  LOCAL_RAIL_HINTS,
};
